import { ActionIcon, Button, Group, Text, Tooltip } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import {
  IconFolder,
  IconPencil,
  IconPlus,
  IconTrash
} from '@tabler/icons-react'
import { FormattedMessage, useIntl } from 'react-intl'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import { LabelGroup } from '@/components/labels/LabelGroup/LabelGroup'
import { useHasPermissions } from '@/permissions/useHasPermissions'
import {
  useCreateDatasetVersion,
  useInvalidateDatasetQueries
} from '@/queries/datasetQueries'
import { showToast } from '@/theme/notifications'
import { Dataset, DatasetVersion, DatasetVersionStatus } from '@/types/dataset'
import { CrudAction, Resource } from '@/types/permissions'
import { DatasetVersionSelect } from '../DatasetVersionSelect/DatasetVersionSelect'
import { DeleteDatasetVersionModal } from '../DeleteDatasetVersionModal/DeleteDatasetVersionModal'
import { UpdateDatasetModal } from '../UpdateDatasetModal/UpdateDatasetModal'

type DatasetToolbarProps = {
  dataset: Dataset
  versions: DatasetVersion[]
  activeVersion?: DatasetVersion
  isVersionPending?: boolean
  onVersionChange: (versionId: string) => void
  onVersionCreated?: (versionId: string) => void
  onVersionDeleted?: () => void
}

export const DatasetToolbar = ({
  dataset,
  versions,
  activeVersion,
  isVersionPending,
  onVersionChange,
  onVersionCreated,
  onVersionDeleted
}: DatasetToolbarProps) => {
  const intl = useIntl()

  const [
    isDeleteVersionModalOpened,
    { open: openDeleteVersionModal, close: closeDeleteVersionModal }
  ] = useDisclosure(false)

  const [
    isUpdateDatasetModalOpened,
    { open: openUpdateDatasetModal, close: closeUpdateDatasetModal }
  ] = useDisclosure(false)

  const { mutateAsync, isPending: isCreatePending } = useCreateDatasetVersion()
  const { invalidateDatasetVersions } = useInvalidateDatasetQueries()

  const [canCreateVersion, canUpdateVersion, canDeleteVersion] =
    useHasPermissions(Resource.Datasets, [
      CrudAction.Create,
      CrudAction.Update,
      CrudAction.Delete
    ])

  const versionSelectOptions = versions.map((version) => ({
    value: version.id,
    label: intl
      .formatMessage(
        { id: 'datasets.versionNumber' },
        { number: version.version }
      )
      .toLowerCase(),
    version
  }))

  const handleVersionCreate = async () => {
    if (!activeVersion) {
      return
    }

    try {
      const version = await mutateAsync({
        datasetId: dataset.id,
        parentVersionId: activeVersion.id
      })

      await invalidateDatasetVersions(dataset.id)

      onVersionCreated?.(version.id)

      showToast(
        intl.formatMessage({ id: 'datasets.version.create.success' }),
        'success'
      )
    } catch (err) {
      showToast(
        extractErrorMessage(
          err,
          intl.formatMessage({ id: 'datasets.version.create.error' })
        ),
        'error'
      )
    }
  }

  return (
    <>
      <Group align="center" justify="space-between" wrap="nowrap" gap="lg">
        <Group wrap="nowrap" align="center" gap="lg">
          <Group wrap="nowrap" gap="xs">
            <IconFolder size={20} />

            <Text size="md" fw={600} miw={100} maw={300} truncate>
              {dataset.name}
            </Text>
          </Group>

          <Group miw={180} wrap="nowrap" gap="xs">
            <DatasetVersionSelect
              value={activeVersion?.id}
              versions={versionSelectOptions}
              isPending={isVersionPending}
              onChange={onVersionChange}
            />

            {/* last version cannot be removed */}
            {versions.length > 1 &&
              activeVersion &&
              activeVersion.status !== DatasetVersionStatus.Completed &&
              canDeleteVersion && (
                <Tooltip
                  label={<FormattedMessage id="datasets.version.delete" />}
                >
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    radius="xl"
                    onClick={openDeleteVersionModal}
                  >
                    <IconTrash style={{ width: '80%', height: '80%' }} />
                  </ActionIcon>
                </Tooltip>
              )}

            {versions.length > 0 &&
              activeVersion &&
              activeVersion.status === DatasetVersionStatus.Completed &&
              canCreateVersion && (
                <Button
                  size="xs"
                  variant="subtle"
                  leftSection={<IconPlus size={16} />}
                  loading={isCreatePending || isVersionPending}
                  onClick={() => void handleVersionCreate()}
                >
                  <FormattedMessage id="datasets.version.create" />
                </Button>
              )}
          </Group>
        </Group>

        <Group wrap="nowrap">
          <LabelGroup labels={dataset.labels} maxLabelsShown={5} />

          {canUpdateVersion && (
            <Tooltip label={<FormattedMessage id="annotation.classes.edit" />}>
              <ActionIcon
                variant="subtle"
                size="md"
                radius="xl"
                onClick={openUpdateDatasetModal}
              >
                <IconPencil style={{ width: '60%', height: '60%' }} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      </Group>

      <DeleteDatasetVersionModal
        opened={isDeleteVersionModalOpened}
        datasetId={dataset.id}
        datasetVersionId={activeVersion?.id}
        onClose={closeDeleteVersionModal}
        onVersionDeleted={onVersionDeleted}
      />

      <UpdateDatasetModal
        opened={isUpdateDatasetModalOpened}
        datasetId={dataset.id}
        onClose={closeUpdateDatasetModal}
      />
    </>
  )
}
